/*
  Product quick view
  ------------------
  Opens a right-hand drawer with a product's images, description and price
  so a shopper reading the Popular Add-ons row never has to leave the page
  they are buying on. Leaving the PDP mid-configuration is expensive here:
  the bundle builder keeps its state in the URL, so a trip to an add-on's
  page and back is a rebuild of the pack.

  Progressive enhancement, deliberately: the markup stays an ordinary
  <a href="/products/handle">, so this file failing to load, or a
  cmd/middle click, still lands the shopper on the real product page.
  Nothing here is required for the link to work.

  Wiring: any <a data-quickview href="/products/...">, anywhere on the
  page, present at load or injected later. The listener is delegated at
  document level, so the add-on pool swapping cards in and out (and the
  cable dropdown rewriting hrefs) needs no re-binding. The handle is read
  off the href at click time, which is what keeps a swapped card honest.

  Styles: assets/product-quickview.css
*/
(function () {
  'use strict';

  var MAX_SLIDES = 8; /* several add-ons carry 11+ images; past ~8 the strip
                         stops informing and starts being a scroll chore */
  var cache = {};     /* handle -> product JSON, so reopening is instant */
  var qv = null;      /* built on first open, reused after */
  var opener = null;  /* element to hand focus back to on close */
  var reqId = 0;      /* guards against a slow fetch landing after a newer one */
  var openSeq = 0;    /* bumped every open; a close in flight belongs to one of
                         them. Escape-then-immediately-open-another lands the
                         old close's cleanup (hidden = true, track emptied) on
                         the NEW drawer otherwise, leaving it invisible but
                         flagged open with no way back. */
  var closeTimer = null;

  /* ---------- helpers ---------- */

  function moneyFormat() {
    var s = document.currentScript;
    if (s && s.dataset.moneyFormat) return s.dataset.moneyFormat;
    var el = document.querySelector('[data-bundle-data]');
    if (el) {
      try {
        var f = JSON.parse(el.textContent).moneyFormat;
        if (f) return f;
      } catch (e) { /* fall through to the default */ }
    }
    return '${{amount}}';
  }
  var FORMAT = moneyFormat();

  /* Same rules as bundle-builder.js formatMoney: shop.money_format in,
     cents in, a display string out. Kept as its own copy rather than
     shared so this file stays droppable on pages with no bundle module. */
  function money(cents) {
    var units = cents / 100;
    var thousands = function (s) { return s.replace(/\B(?=(\d{3})+(?!\d))/g, ','); };
    return FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      switch (key) {
        case 'amount_no_decimals':
          return thousands(String(Math.round(units)));
        case 'amount_with_comma_separator':
          return units.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        case 'amount_no_decimals_with_comma_separator':
          return String(Math.round(units)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        default:
          return thousands(units.toFixed(2));
      }
    });
  }

  function handleFromHref(href) {
    if (!href) return '';
    var m = String(href).split('?')[0].split('#')[0].match(/\/products\/([^/]+)\/?$/);
    return m ? m[1] : '';
  }

  /* Ask the CDN for the size we actually paint instead of the 2000px
     original: the drawer never shows an image wider than ~500 CSS px. */
  function sized(url, w) {
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  /* Descriptions are merchant HTML, so they are trusted the way theme
     markup is, but they also pass through an admin field that anyone with
     staff access can edit. Dropping executable nodes and inline handlers
     costs nothing and keeps a stray paste from becoming a script on the
     storefront. */
  function safeHtml(html) {
    var doc = new DOMParser().parseFromString('<div>' + (html || '') + '</div>', 'text/html');
    var root = doc.body.firstChild;
    root.querySelectorAll('script, style, iframe, object, embed, form, link, meta').forEach(function (n) {
      n.remove();
    });
    root.querySelectorAll('*').forEach(function (n) {
      Array.prototype.slice.call(n.attributes).forEach(function (a) {
        var name = a.name.toLowerCase();
        var val = (a.value || '').replace(/\s+/g, '').toLowerCase();
        if (name.indexOf('on') === 0) n.removeAttribute(a.name);
        if ((name === 'href' || name === 'src') && val.indexOf('javascript:') === 0) n.removeAttribute(a.name);
      });
    });
    return root.innerHTML;
  }

  function scrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  /* ---------- build ---------- */

  function build() {
    var el = document.createElement('div');
    el.className = 'tt-qv';
    el.hidden = true;
    el.innerHTML = [
      '<div class="tt-qv__panel" role="dialog" aria-modal="true" aria-labelledby="tt-qv-title" tabindex="-1">',
      '<button type="button" class="tt-qv__close" data-qv-close aria-label="Close">',
      '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 4 L16 16 M16 4 L4 16" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>',
      '</button>',
      '<div class="tt-qv__scroll" data-qv-scroll>',
      '<div class="tt-qv__media" data-qv-media>',
      '<div class="tt-qv__track" data-qv-track></div>',
      '<div class="tt-qv__nav" data-qv-nav hidden>',
      '<button type="button" class="tt-qv__arrow" data-qv-prev aria-label="Previous image"><svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M12 4 L6 10 L12 16" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg></button>',
      '<span class="tt-qv__count" data-qv-count aria-hidden="true"></span>',
      '<button type="button" class="tt-qv__arrow" data-qv-next aria-label="Next image"><svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M8 4 L14 10 L8 16" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg></button>',
      '</div>',
      '</div>',
      '<h2 class="tt-qv__title" id="tt-qv-title"></h2>',
      '<p class="tt-qv__price" data-qv-price></p>',
      '<div class="tt-qv__desc" data-qv-desc></div>',
      '<a class="tt-qv__full" data-qv-full href="#">View full details</a>',
      '</div>',
      '<p class="tt-qv__status" data-qv-status role="status"></p>',
      '</div>'
    ].join('');
    document.body.appendChild(el);

    qv = {
      root: el,
      panel: el.querySelector('.tt-qv__panel'),
      scroll: el.querySelector('[data-qv-scroll]'),
      media: el.querySelector('[data-qv-media]'),
      track: el.querySelector('[data-qv-track]'),
      nav: el.querySelector('[data-qv-nav]'),
      count: el.querySelector('[data-qv-count]'),
      title: el.querySelector('.tt-qv__title'),
      price: el.querySelector('[data-qv-price]'),
      desc: el.querySelector('[data-qv-desc]'),
      full: el.querySelector('[data-qv-full]'),
      status: el.querySelector('[data-qv-status]'),
      index: 0
    };

    el.querySelector('[data-qv-close]').addEventListener('click', close);
    /* The scrim is .tt-qv's own ::before, not a child element, so a click
       anywhere outside the panel lands on the root itself. This store runs
       a third-party stylesheet with `div:empty { display: none }`, which
       silently collapsed an empty overlay div — a pseudo-element cannot be
       caught by it, and is one node lighter besides. */
    el.addEventListener('click', function (e) { if (e.target === el) close(); });
    el.querySelector('[data-qv-prev]').addEventListener('click', function () { step(-1); });
    el.querySelector('[data-qv-next]').addEventListener('click', function () { step(1); });
    qv.track.addEventListener('scroll', updateCount, { passive: true });

    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
      if (e.key === 'Tab') trapTab(e);
    });

    return qv;
  }

  function trapTab(e) {
    var f = qv.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var list = Array.prototype.filter.call(f, function (n) { return n.offsetParent !== null; });
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- slideshow ----------
     Native scroll-snap rather than a slider library: the drawer is one
     axis of images, and scroll-snap gives touch swipe, momentum, keyboard
     and accessible scrolling for free. The arrows just nudge the scroller. */

  function setCount(i, total) {
    qv.count.textContent = (i + 1) + ' / ' + total;
  }

  /* Absolute scrollTo, never the relative scrollBy that reads more naturally
     here: against a mandatory snap container a relative scroll is measured
     from a position the snapper is still adjusting, so the second arrow press
     in a row silently did nothing. An absolute offset lands every time. The
     index is therefore ours to own, not something to re-derive from
     scrollLeft on the way in. */
  function goTo(i, smooth) {
    var total = qv.track.children.length;
    if (!total) return;
    var w = qv.track.clientWidth;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    qv.index = Math.min(total - 1, Math.max(0, i));
    qv.track.scrollTo({ left: qv.index * w, behavior: smooth && !reduce ? 'smooth' : 'auto' });
    setCount(qv.index, total);
  }

  function step(dir) {
    goTo((qv.index || 0) + dir, true);
  }

  /* Swipes and trackpad scrolls come through here. The arrows set the label
     themselves, so it never trails the animation. */
  function updateCount() {
    var w = qv.track.clientWidth;
    var total = qv.track.children.length;
    if (!w || !total) return;
    qv.index = Math.min(total - 1, Math.max(0, Math.round(qv.track.scrollLeft / w)));
    setCount(qv.index, total);
  }

  /* ---------- render ---------- */

  function renderLoading() {
    qv.track.innerHTML = '';
    qv.nav.hidden = true;
    qv.title.textContent = '';
    qv.price.textContent = '';
    qv.desc.innerHTML = '';
    qv.media.classList.add('is-loading');
    qv.status.textContent = 'Loading';
  }

  function render(p) {
    qv.media.classList.remove('is-loading');
    qv.status.textContent = '';

    var media = (p.media || []).filter(function (m) { return m.media_type === 'image'; }).slice(0, MAX_SLIDES);
    qv.track.innerHTML = media.map(function (m, i) {
      var src = sized(m.src, 900);
      return '<div class="tt-qv__slide">' +
        '<img src="' + src + '" srcset="' + sized(m.src, 600) + ' 600w, ' + src + ' 900w, ' + sized(m.src, 1200) + ' 1200w"' +
        ' sizes="(max-width: 749px) 100vw, 500px"' +
        ' alt="' + String(m.alt || p.title || '').replace(/"/g, '&quot;') + '"' +
        ' loading="' + (i === 0 ? 'eager' : 'lazy') + '" decoding="async">' +
        '</div>';
    }).join('');
    qv.track.scrollLeft = 0;
    qv.index = 0;
    qv.nav.hidden = media.length < 2;
    if (media.length > 1) setCount(0, media.length);

    qv.title.textContent = p.title || '';

    var now = p.price;
    var was = p.compare_at_price;
    qv.price.innerHTML = (was && was > now ? '<s class="tt-qv__was">' + money(was) + '</s>' : '') +
      '<span class="tt-qv__now">' + money(now) + '</span>';

    qv.desc.innerHTML = safeHtml(p.description);
    qv.full.href = '/products/' + p.handle;
  }

  /* ---------- open / close ---------- */

  function open(handle, from) {
    if (!handle) return;
    if (!qv) build();
    opener = from || null;

    var id = ++reqId;
    openSeq++;
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    qv.root.hidden = false;
    /* The transition needs a start position committed before the end state
       is set. A forced reflow does that synchronously; requestAnimationFrame
       reads more naturally but never fires in a backgrounded or throttled
       tab, which left the drawer unhidden, focus-trapped and scroll-locked
       while still parked off-screen. */
    void qv.root.offsetWidth;
    qv.root.classList.add('is-open');

    var pad = scrollbarWidth();
    document.documentElement.classList.add('tt-qv-open');
    if (pad > 0) document.documentElement.style.setProperty('--tt-qv-pad', pad + 'px');

    qv.panel.focus();

    if (cache[handle]) { render(cache[handle]); return; }

    renderLoading();
    fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (p) {
        cache[handle] = p;
        if (id === reqId && !qv.root.hidden) render(p);
      })
      .catch(function () {
        if (id !== reqId) return;
        /* No invented copy and no dead end: say it plainly and leave the
           real product page one tap away. */
        qv.media.classList.remove('is-loading');
        qv.status.textContent = 'Could not load this product.';
        qv.full.href = '/products/' + handle;
      });
  }

  function close() {
    if (!qv || qv.root.hidden) return;
    reqId++;
    qv.root.classList.remove('is-open');
    document.documentElement.classList.remove('tt-qv-open');
    document.documentElement.style.removeProperty('--tt-qv-pad');

    var seq = openSeq;
    var done = function () {
      if (seq !== openSeq) return; /* reopened mid-animation: not ours to clean */
      qv.root.hidden = true;
      qv.track.innerHTML = ''; /* stop offscreen images decoding */
      if (opener && document.contains(opener)) opener.focus();
      opener = null;
    };
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { done(); return; }
    closeTimer = setTimeout(done, 400);
    qv.panel.addEventListener('transitionend', function once(e) {
      if (e.target !== qv.panel || e.propertyName !== 'transform') return;
      clearTimeout(closeTimer);
      closeTimer = null;
      qv.panel.removeEventListener('transitionend', once);
      done();
    });
  }

  /* ---------- wiring ---------- */

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[data-quickview]');
    if (!link) return;
    /* leave every "open somewhere else" gesture alone */
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var handle = handleFromHref(link.getAttribute('href'));
    if (!handle) return; /* not a product URL: let the link do its job */
    e.preventDefault();
    open(handle, link);
  });
}());
